/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
    arrayOf, bool, number, shape, string, func, node, object,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Pill from '../../../ProductFacet/Pill';
import ProductFacetDesktop from '../../../ProductFacet/ProductFacetDesktop';
import ProductFacetMobile from '../../../ProductFacet/ProductFacetMobile';
import ProductFacetMobileSheet from '../../../ProductFacet/ProductFacetMobileSheet';
import SelectedProductFacets from '../../../ProductFacet/SelectedProductFacets';
import ProductCount from '../../../ProductFacet/ProductCount';
import useCheckedFacets from '../../../../../helpers/hooks/useCheckedFacets';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getCurrentPageType } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import { trackEvent as trackGa4 } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import scrollToTop from '../GraphqlProductFacets/scrollToTop';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useExperience from '../../../../../helpers/Personalization/useExperience';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import GenericSkeleton from '../../../GraphqlSkeletonComponents/GenericSkeleton';
import ProductFacetSkeleton from '../../../GraphqlSkeletonComponents/ProductFacetSkeleton';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    actionRow: {
        display: 'flex',
        gap: '8px',
        width: '100%',
        overflowX: 'auto',
        paddingBottom: '16px',
        paddingTop: '16px',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        scrollbarWidth: 'none',
    },
    selectedWrapper: {
        borderTop: '1px solid #E5E5E5',
        paddingBottom: '16px',
        paddingTop: '16px',
    },
}));

const SORT_BY_OPTIONS = [
    // { label: 'Featured', value: 'FEATURED' },
    { label: 'Price: Low to High', value: 'PRICE_ASC' },
    { label: 'Price: High to Low', value: 'PRICE_DESC' },
    // { label: 'Newest Arrivals', value: 'NEW_ARRIVALS' },
    { label: 'Best Sellers', value: 'BEST_SELLERS' },
];
const DESKTOP_FILTERS_SELECTOR = '[data-product-desktop-filters-v4="true"]';

const SortByMobile = ({
    options,
    selectedValue,
    onApply,
    children,
    stateless,
    loading,
    trackEvent,
    resultsCount,
}) => {
    const entries = options.map((option) => ({
        name: option.label,
        value: option.value,
        count: 0,
        from: null,
        to: null,
        tooltip: null,
    }));

    return (
        <ProductFacetMobileSheet
            title="Sort By"
            entries={entries}
            selectedValues={selectedValue ? [selectedValue] : []}
            onApply={(values) => {
                if (values.length) onApply(values[0]);
            }}
            itemType="radio"
            stateless={stateless}
            loading={loading}
            trackEvent={trackEvent}
            resultsCount={resultsCount}
        >
            {children}
        </ProductFacetMobileSheet>
    );
};

SortByMobile.propTypes = {
    options: arrayOf(shape({
        label: string.isRequired,
        value: string.isRequired,
    })).isRequired,
    selectedValue: string,
    onApply: func.isRequired,
    children: node.isRequired,
    stateless: bool,
    loading: bool,
    trackEvent: func,
    resultsCount: number,
};

SortByMobile.defaultProps = {
    selectedValue: null,
    stateless: false,
    loading: false,
    trackEvent: () => {},
    resultsCount: null,
};

const ProductFacetsV4 = ({
    productFacets,
    isDesktop,
    productCount,
    loading,
    setFacetsDataInCollection,
    category,
    selectedSortValue,
    onSortChange,
    disableSort,
    searchPage,
    searchTerm,
    filterSize,
}) => {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const checkedFacetsData = useCheckedFacets();
    const pageType = useSelector(getCurrentPageType);
    const isAggregatorFacetErrorResolver = useSelector(getFeatureFlag('is-facet-throwing-error-resolver-enabled'));
    const categoryId = category?.id;
    const categoryName = category?.name;
    const pageSearch = history.location?.search;
    const maximumNoOfFilters = filterSize?.maximum_no_of_filters || 7;
    const brand = useSelector(getBrand);
    const { loading: hideDeliveryTypeLoading, variables, data: hideDeliveryTypeData } = useExperience({
        queryName: 'hideDeliveryType',
        interactionName: `${brand.domain}-hide-delivery-type-FPT-GPT`,
        type: 'collection',
    });

    const deliveryTypeGroup = hideDeliveryTypeData?.experience?.experiment;
    const hideDeliveryType = deliveryTypeGroup?.value;
    const deliveryTypeTracking = hideDeliveryTypeData?.experience?.experimentTracking;
    const productFacetData = hideDeliveryType ? productFacets?.filter((facet) => facet?.name !== 'Delivery Option') : productFacets;
    useClickStreamCustomEventsTracking({
        deliveryTypeTracking,
        isFireImpression: true,
        page: 'collection',
    });
    useEffect(() => {
        if (typeof deliveryTypeGroup !== 'undefined' && !window.wuHideDeliveryTypeFired && deliveryTypeGroup) {
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Hide Delivery Type',
                eventLabel: hideDeliveryType ? 'Variant' : 'Control',
                feature_detail: hideDeliveryType ? 'Variant' : 'Control',
                feature_element: 'Hide Delivery Type',
                feature_category: 'Experiment',
            };
            dispatch(trackGa4(eventParam));
            window.wuHideDeliveryTypeFired = true;
        }
        return () => {
            window.wuHideDeliveryTypeFired = false;
        };
    }, [deliveryTypeGroup]);

    const maximumNoOfQuickFilters = filterSize?.maximum_no_of_quick_filters || 3;
    const visibleProductFacets = useMemo(
        () => productFacetData?.slice(0, maximumNoOfFilters) || [],
        [maximumNoOfFilters, productFacetData],
    );
    const quickProductFacets = useMemo(
        () => visibleProductFacets?.slice(0, maximumNoOfQuickFilters) || [],
        [maximumNoOfQuickFilters, visibleProductFacets],
    );

    const searchValue = unescape(history.location?.search);

    const [selectedProductFacets, setSelectedProductFacets] = useState(checkedFacetsData);

    useEffect(() => {
        setSelectedProductFacets(checkedFacetsData);
        setFacetsDataInCollection(checkedFacetsData);
    }, [searchValue]);

    const selectedFacets = useMemo(() => (
        selectedProductFacets
            .map((value) => {
                for (let i = 0; i < visibleProductFacets.length; i += 1) {
                    const facet = visibleProductFacets[i];
                    const entry = facet.entries.find((e) => e.value === value);
                    if (entry) {
                        return { label: entry.name, value: entry.value };
                    }
                }
                return null;
            })
            .filter((item) => item !== null)
    ), [selectedProductFacets, visibleProductFacets]);

    const selectedSortFacet = useMemo(() => {
        if (disableSort) return null;
        if (!selectedSortValue) return null;
        const selectedSort = SORT_BY_OPTIONS.find((option) => option.value === selectedSortValue);
        return {
            label: selectedSort?.label || selectedSortValue,
            value: selectedSortValue,
        };
    }, [disableSort, selectedSortValue]);

    const timerId = useRef(null);
    const rootRef = useRef(null);
    const hasTrackedDesktopImpressionRef = useRef(false);

    const yieldToMain = () => {
        // Use scheduler.yield if it exists:
        if (typeof window !== 'undefined' && 'scheduler' in window && 'yield' in window.scheduler) {
            return window.scheduler.yield();
        }

        // Fall back to setTimeout:
        return new Promise((resolve) => setTimeout(resolve, 0));
    };

    const historyReplace = async (searchParam) => {
        history.replace({
            search: searchParam,
            state: selectedProductFacets,
        });
        await yieldToMain();
    };

    const scrollToRootOrTop = useCallback(() => {
        const isElementInViewport = (element) => {
            if (!element || typeof window === 'undefined') {
                return false;
            }
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            return rect.top < viewportHeight && rect.bottom > 0;
        };

        if (isDesktop) {
            if (typeof document !== 'undefined' && typeof window !== 'undefined') {
                const desktopFiltersElement = document.querySelector(DESKTOP_FILTERS_SELECTOR);
                if (desktopFiltersElement && typeof desktopFiltersElement.scrollIntoView === 'function') {
                    if (isElementInViewport(desktopFiltersElement)) {
                        return;
                    }
                    desktopFiltersElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest',
                    });
                    return;
                }
            }
            scrollToTop();
            return;
        }
        if (!isDesktop && rootRef.current && typeof rootRef.current.scrollIntoView === 'function') {
            if (isElementInViewport(rootRef.current)) {
                return;
            }
            rootRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });
            return;
        }
        scrollToTop();
    }, [isDesktop]);

    const isElementVisible = useCallback((element) => {
        if (!element || typeof window === 'undefined') {
            return false;
        }

        const styles = window.getComputedStyle(element);
        if (styles.display === 'none' || styles.visibility === 'hidden') {
            return false;
        }

        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
    }, []);

    useEffect(() => {
        let delimiter = '&';
        if (isAggregatorFacetErrorResolver) {
            delimiter = '&facet=';
        }
        const urlSelectedParams = selectedProductFacets.join(delimiter);
        clearTimeout(timerId.current);
        timerId.current = setTimeout(() => { // delay the history for two ms
            let searchParam = '';
            if (urlSelectedParams) {
                searchParam = `facet=${urlSelectedParams}`;
            } else if (searchValue && !searchValue.includes('facet')) {
                searchParam = searchValue;
            }
            historyReplace(searchParam); // for scheduler yield
        }, 50);
    }, [selectedProductFacets?.length]);

    const handleFilterChange = (_facetName, value, checked) => {
        setSelectedProductFacets((prev) => {
            if (checked) return [...prev, value];
            return prev.filter((v) => v !== value);
        });
        scrollToRootOrTop();
    };

    const handleSortChange = (value) => {
        if (disableSort) return;
        onSortChange(value);
        scrollToRootOrTop();
    };

    const handleApply = (selectedFacetsValues) => {
        setSelectedProductFacets(selectedFacetsValues);
        scrollToRootOrTop();
    };

    const handleRemove = (value) => {
        setSelectedProductFacets((prev) => prev.filter((v) => v !== value));
        scrollToRootOrTop();
    };

    const handleRemoveSort = () => {
        if (disableSort) return;
        onSortChange('');
        scrollToRootOrTop();
    };

    const handleClearAll = () => {
        setSelectedProductFacets([]);
        if (!disableSort) {
            onSortChange('');
        }
        setFacetsDataInCollection([]);
        scrollToRootOrTop();
    };

    const handleFacetMobileApply = (prevSelectedValues, currentSelectedValues) => {
        setSelectedProductFacets((prev) => prev
            .filter((v) => !prevSelectedValues.includes(v))
            .concat(currentSelectedValues));
        scrollToRootOrTop();
    };

    const genProductFilterForCSEvent = (facet, sortBy) => {
        const productFilters = {};
        if (facet?.value?.includes('|')) {
            productFilters['facets'] = [facet?.value.replace('|', '-')];
        }
        if (facet?.value?.includes('-') && facet?.name?.includes('$')) {
            productFilters['priceRangeFilters'] = [facet?.value];
        }
        if (sortBy) {
            productFilters['orderBy'] = sortBy;
        }
        return productFilters;
    };

    const trackCustomClickstreamEventByType = useCallback((actionType = 'Impression', data = null, sortBy = null) => {
        const action = actionType || 'Impression';
        // Fire facet interaction event
        if (action === 'Click') {
            if (pageType === 'category' && categoryId && data) {
                const trackData = {
                    eventCategory: 'Collection Page',
                    eventAction: `Filter Interaction - V4 - ${categoryName}`,
                    eventLabel: data.value,
                    category_name: categoryName,
                    category_id: categoryId,
                    filters_product_count: data.count,
                    filters_page_url: pageSearch,
                    eventName: 'apply_filter',
                    filter_name: data?.name || '',
                };
                dispatch(trackGa4(trackData));
            }
            if (pageType === 'search' && data) {
                const trackData = {
                    eventCategory: 'Search Page',
                    eventAction: `Filter Interaction - V4 - ${data?.name || data?.value}`,
                    eventLabel: data?.value,
                    filters_product_count: data?.count,
                    filters_page_url: pageSearch,
                    eventName: 'apply_filter',
                    filter_name: data?.name  || '',
                };
                dispatch(trackGa4(trackData));
            }
            console.log('data', { data });
            dispatch(emitCustomTrackingEvent({
                eventType: 'custom',
                eventSubType: 'feature-tracking',
                action: 'Click',
                category: pageType === 'search' ? 'Search' : 'Browse',
                label: 'filter_facet',
                productFilters: genProductFilterForCSEvent(data, sortBy),
            }));
        }
    }, [categoryId, categoryName, dispatch, pageSearch, pageType]);

    useEffect(() => {
        if (!isDesktop || hasTrackedDesktopImpressionRef.current) {
            return undefined;
        }

        const tryTrackDesktopImpression = () => {
            if (hasTrackedDesktopImpressionRef.current || !isElementVisible(rootRef.current)) {
                return;
            }

            hasTrackedDesktopImpressionRef.current = true;
            trackCustomClickstreamEventByType('Impression');
        };

        tryTrackDesktopImpression();

        if (hasTrackedDesktopImpressionRef.current) {
            return undefined;
        }

        window.addEventListener('resize', tryTrackDesktopImpression);
        return () => {
            window.removeEventListener('resize', tryTrackDesktopImpression);
        };
    }, [isDesktop, isElementVisible, trackCustomClickstreamEventByType]);
    if (hideDeliveryTypeLoading || variables?.skip) {
        return isDesktop ? <ProductFacetSkeleton /> : '';
    }
    return isDesktop ? (
        <div className={classes.root} ref={rootRef}>
            <ProductFacetDesktop
                title="Filters"
                productFacets={visibleProductFacets}
                selectedProductFacets={selectedProductFacets}
                onChange={handleFilterChange}
                itemType="checkbox"
                loading={loading}
                sortByOptions={disableSort ? [] : SORT_BY_OPTIONS}
                selectedSortValue={disableSort ? '' : selectedSortValue}
                onSortChange={disableSort ? undefined : handleSortChange}
                trackEvent={trackCustomClickstreamEventByType}
                searchPage={searchPage}
            />
        </div>
    ) : (
        <div className={classes.root} ref={rootRef}>
            <div className={classes.actionRow}>
                <ProductFacetMobile
                    productFacets={visibleProductFacets}
                    selectedProductFacets={selectedProductFacets}
                    onApply={handleApply}
                    sortByOptions={disableSort ? [] : SORT_BY_OPTIONS}
                    selectedSortValue={disableSort ? '' : selectedSortValue}
                    onSortChange={disableSort ? undefined : handleSortChange}
                    endIconType="filter"
                    stateless
                    loading={loading}
                    trackEvent={trackCustomClickstreamEventByType}
                    resultsCount={productCount}
                >
                    <Pill
                        label="Filter"
                        endIconType="filter"
                        variant={selectedFacets.length > 0 ? 'active' : 'inactive'}
                        badgeCount={selectedFacets.length}
                    />
                </ProductFacetMobile>
                {!disableSort && (
                    <SortByMobile
                        options={SORT_BY_OPTIONS}
                        selectedValue={selectedSortValue}
                        onApply={handleSortChange}
                        endIconType="sortby"
                        stateless
                        loading={loading}
                        trackEvent={trackCustomClickstreamEventByType}
                        resultsCount={productCount}
                    >
                        <Pill
                            label="Sort"
                            endIconType="sortby"
                            variant={selectedSortValue === '' ? 'inactive' : 'active'}
                        />
                    </SortByMobile>
                )}
                {quickProductFacets?.map((facet) => (
                    <ProductFacetMobileSheet
                        key={facet.name}
                        title={facet.name}
                        entries={facet.entries}
                        selectedValues={selectedProductFacets.filter(
                            (facetValue) => facet.entries.some((entry) => entry.value === facetValue),
                        )}
                        onApply={(appliedValues) => handleFacetMobileApply(
                            selectedProductFacets.filter(
                                (facetValue) => facet.entries.some((entry) => entry.value === facetValue),
                            ),
                            appliedValues,
                        )}
                        endIconType="dropdown"
                        stateless
                        loading={loading}
                        trackEvent={trackCustomClickstreamEventByType}
                        resultsCount={productCount}
                    >
                        <Pill
                            label={facet.name}
                            endIconType="dropdown"
                            variant={selectedProductFacets.some(
                                (facetValue) => facet.entries.some((entry) => entry.value === facetValue),
                            ) ? 'active' : 'inactive'}
                        />
                    </ProductFacetMobileSheet>
                ))}
            </div>
            {(selectedFacets.length > 0 || !!selectedSortFacet) && (
                <div className={classes.selectedWrapper}>
                    <SelectedProductFacets
                        selectedFacets={selectedFacets}
                        selectedSortFacet={selectedSortFacet}
                        onRemove={handleRemove}
                        onRemoveSort={handleRemoveSort}
                        onClearAll={handleClearAll}
                        loading={loading}
                    />
                </div>
            )}
            <ProductCount productCount={productCount} searchTerm={searchTerm} />
        </div>
    );
};

ProductFacetsV4.propTypes = {
    productFacets: arrayOf(shape({
        name: string.isRequired,
        displayOpen: bool,
        entries: arrayOf(shape({
            name: string.isRequired,
            value: string.isRequired,
            count: number,
            from: number,
            to: number,
            tooltip: string,
        })),
    })),
    isDesktop: bool,
    productCount: number,
    loading: bool,
    setFacetsDataInCollection: func,
    category: object,
    selectedSortValue: string,
    onSortChange: func,
    disableSort: bool,
    searchPage: bool,
    searchTerm: string,
    filterSize: object,
};

ProductFacetsV4.defaultProps = {
    productFacets: [],
    isDesktop: false,
    setFacetsDataInCollection: () => {},
    productCount: null,
    loading: false,
    category: {},
    selectedSortValue: '',
    onSortChange: () => {},
    disableSort: false,
    searchPage: false,
    searchTerm: '',
    filterSize: {},
};

export default ProductFacetsV4;
